import { useState } from 'react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import CanonicalTag from '@/components/CanonicalTag';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Calendar, Home, DollarSign, Building, HelpCircle, PenTool, BookOpen, Users, Moon } from 'lucide-react';

const HowToUseADUAsWritersRetreatBnb = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  const ctaButtons = [
    {
      icon: Calendar,
      text: "Book & Schedule",
      onClick: () => setShowCalendly(true)
    },
  {
    icon: Building,
    text: "What Fits My Lot?",
    onClick: () => window.open('/landing', '_self')
  },
  {
    icon: DollarSign,
    text: "Get Price & ROI Report",
    onClick: () => window.open('/adu-calculator', '_self')
  },
  {
    icon: Home,
    text: "Our Portfolio",
    onClick: () => window.open('/our-work', '_self')
  },
  {
    icon: HelpCircle,
    text: "Our FAQ",
    onClick: () => window.open('/faq', '_self')
  }
];


  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-as-writers-retreat-bnb" />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
            How to Use Your ADU as a Writer's Retreat or Quiet Airbnb Studio
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Appeal to traveling creatives, digital detoxers, and writers-in-residence with a minimalist, private ADU tailored for focus.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {ctaButtons.map((button, index) => (
              <Button
                key={index}
                onClick={button.onClick}
                className="flex items-center gap-2"
                style={{ backgroundColor: index === 0 ? '#E4683B' : 'transparent', borderColor: '#E4683B', color: index === 0 ? 'white' : '#E4683B' }}
                variant={index === 0 ? "default" : "outline"}
              >
                <button.icon className="w-4 h-4" />
                {button.text}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Quiet Space Section */}
      <section className="py-16 px-4 bg-secondary/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>A Quiet Space for Guests Who Crave Stillness</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <PenTool className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Writers, poets, journalists</span>
                </div>
                <div className="flex items-center gap-3">
                  <Moon className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Booked-for-silence travelers</span>
                </div>
                <div className="flex items-center gap-3">
                  <BookOpen className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Long-stay creatives on sabbatical</span>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Digital detox seekers</span>
                </div>
              </div>
            </div>
            <div className="bg-muted rounded-lg p-8">
              <img 
                src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=800&h=400" 
                alt="Writer's Retreat ADU" 
                className="w-full h-64 object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Target Guest Profiles Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#E4683B' }}>Target Guest Profiles</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <PenTool className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Creative Writers</h3>
              <p className="text-muted-foreground">Authors seeking inspiration and uninterrupted writing time</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Moon className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Digital Detoxers</h3>
              <p className="text-muted-foreground">Guests looking to disconnect and find mental clarity</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <BookOpen className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Academics</h3>
              <p className="text-muted-foreground">Researchers and scholars needing focused study time</p>
            </div>
          </div>
        </div>
      </section>

      {/* Recommended Features Section */}
      <section className="py-16 px-4 bg-secondary/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>Recommended Features</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Writing Environment</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Writing desk with deep window light</li>
                <li>• No TV or digital noise</li>
                <li>• Bookshelf wall and cozy reading chair</li>
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Atmosphere</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Neutral color palette and blackout shades</li>
                <li>• Sound-dampening materials</li>
                <li>• Natural lighting and peaceful views</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
            Create a Haven for Creative Minds
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Build a tranquil retreat that attracts guests seeking inspiration and focus.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {ctaButtons.map((button, index) => (
              <Button
                key={index}
                onClick={button.onClick}
                className="flex items-center gap-2"
                style={{ backgroundColor: index === 0 ? '#E4683B' : 'transparent', borderColor: '#E4683B', color: index === 0 ? 'white' : '#E4683B' }}
                variant={index === 0 ? "default" : "outline"}
              >
                <button.icon className="w-4 h-4" />
                {button.text}
              </Button>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* Calendly Modal */}
      <Dialog open={showCalendly} onOpenChange={setShowCalendly}>
        <DialogContent className="max-w-4xl w-full h-[600px] p-0">
          <div className="w-full h-full">
            <iframe
              src="https://calendly.com/udaadu"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Schedule a consultation"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HowToUseADUAsWritersRetreatBnb;